import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Encounter, LabRequest, RadiologyRequest } from '../../types';
import { Search, FileText, FlaskConical, Scan, Printer, Download, X } from 'lucide-react';
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest, isAMHTest } from '../../store/labStructures';
import jsPDF from 'jspdf';
interface Props {}
export const PatientReports: React.FC<Props> = () => {
  const {
    encounters,
    patients
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewing, setViewing] = useState<{
    encounter: Encounter;
  } | null>(null);
  // Encounters that have at least one completed lab OR a radiology report
  const reportEncounters = useMemo(() => {
    return encounters.filter(e => {
      const hasCompletedLab = e.labRequests.some(l => l.status === 'Completed');
      const hasRadiologyReport = e.radiologyRequests.some(r => r.report || r.images && r.images.length > 0);
      return hasCompletedLab || hasRadiologyReport;
    }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }, [encounters]);
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return reportEncounters;
    return reportEncounters.filter(e => {
      const p = patients.find(pt => pt.id === e.patientId);
      if (!p) return false;
      return p.firstName.toLowerCase().includes(q) || p.lastName.toLowerCase().includes(q) || p.bmcFileNumber.toLowerCase().includes(q);
    });
  }, [reportEncounters, patients, searchQuery]);
  const getPatient = (id: string) => patients.find(p => p.id === id);
  const handlePrint = () => window.print();
  const handleDownloadPDF = () => {
    if (!viewing) return;
    const patient = getPatient(viewing.encounter.patientId);
    if (!patient) return;
    const pdf = new jsPDF({
      unit: 'pt',
      format: 'a4'
    });
    const margin = 40;
    let y = margin;
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Patient Report', margin, y);
    y += 22;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${patient.firstName} ${patient.lastName} · BMC ${patient.bmcFileNumber} · ${patient.age} yrs · ${patient.sex}`, margin, y);
    y += 14;
    pdf.text(`Visit date: ${new Date(viewing.encounter.startedAt).toLocaleString()}`, margin, y);
    y += 20;
    const lineCheck = () => {
      const ph = pdf.internal.pageSize.getHeight() - margin;
      if (y > ph) {
        pdf.addPage();
        y = margin;
      }
    };
    // ── Laboratory results ──
    const completedLabs = viewing.encounter.labRequests.filter(l => l.status === 'Completed');
    if (completedLabs.length > 0) {
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Laboratory Results', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      completedLabs.forEach(lab => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
        pdf.text(`Tests: ${activeTests.join(', ')}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (lab.completedAt) {
          pdf.text(`Completed: ${new Date(lab.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        if (lab.results) {
          Object.entries(lab.results).forEach(([k, v]) => {
            if (!v) return;
            lineCheck();
            const line = `  ${k}: ${v}`;
            const lines = pdf.splitTextToSize(line, 515);
            pdf.text(lines, margin, y);
            y += lines.length * 11;
          });
        }
        if (lab.otherTests) {
          lineCheck();
          pdf.text(`  Other: ${lab.otherTests}`, margin, y);
          y += 12;
        }
        if (lab.labComments) {
          lineCheck();
          pdf.setFont('helvetica', 'bold');
          pdf.text('Comments / Observations:', margin, y);
          y += 12;
          pdf.setFont('helvetica', 'normal');
          const lines = pdf.splitTextToSize(lab.labComments, 515);
          lines.forEach((line: string) => {
            lineCheck();
            pdf.text(line, margin, y);
            y += 11;
          });
        }
        y += 6;
      });
    }
    // ── Radiology results ──
    const radWithReport = viewing.encounter.radiologyRequests.filter(r => r.report);
    if (radWithReport.length > 0) {
      lineCheck();
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Radiology Reports', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      radWithReport.forEach(r => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Scan: ${r.scanType}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (r.completedAt) {
          pdf.text(`Completed: ${new Date(r.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        const lines = pdf.splitTextToSize(r.report || '', 515);
        lines.forEach((line: string) => {
          lineCheck();
          pdf.text(line, margin, y);
          y += 11;
        });
        y += 8;
      });
    }
    pdf.save(`report-${patient.bmcFileNumber}-${new Date(viewing.encounter.startedAt).toISOString().slice(0, 10)}.pdf`);
  };
  // ── Renderers for the View modal ──
  const renderLabResults = (lab: LabRequest) => {
    if (!lab.results) return null;
    const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
    const focusedTest = activeTests[0];
    if (isFBCTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5471">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5472">
            FBC Report
          </h4>
          {fbcStructure.map(cat => <div key={cat.category} className="mb-3" data-id="element-5473">
              <p className="font-semibold text-gray-700 mb-1" data-id="element-5474">{cat.category}</p>
              <table className="w-full text-xs border border-gray-300" data-id="element-5475">
                <thead className="bg-gray-100" data-id="element-5476">
                  <tr data-id="element-5477">
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-5478">
                      Test
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-24" data-id="element-5479">
                      Result
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-5480">
                      Reference
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-20" data-id="element-5481">
                      Unit
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-5482">
                  {cat.tests.map(t => <tr key={t.name} data-id="element-5483">
                      <td className="border border-gray-300 p-1.5" data-id="element-5484">{t.name}</td>
                      <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5485">
                        {lab.results![t.name] || '—'}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5486">
                        {t.range}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5487">
                        {t.unit}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>)}
        </div>;
    }
    if (isUrinalysisTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5488">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5489">
            Urinalysis Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5490">
            <thead className="bg-gray-100" data-id="element-5491">
              <tr data-id="element-5492">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5493">
                  Parameter
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-32" data-id="element-5494">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5495">
                  Reference
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5496">
              {urinalysisTests.map(t => <tr key={t.name} data-id="element-5497">
                  <td className="border border-gray-300 p-1.5" data-id="element-5498">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5499">
                    {lab.results![`UA_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5500">
                    {t.normal || '—'}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isTestosteroneTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5501">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5502">
            Testosterone Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5503">
            <thead className="bg-gray-100" data-id="element-5504">
              <tr data-id="element-5505">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5506">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5507">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5508">
                  Reference (M)
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5509">
                  Reference (F)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5510">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5511">
              {testosteroneTests.map(t => <tr key={t.name} data-id="element-5512">
                  <td className="border border-gray-300 p-1.5" data-id="element-5513">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5514">
                    {lab.results![`TESTO_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5515">
                    {t.rangeMale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5516">
                    {t.rangeFemale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5517">
                    {t.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isAMHTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5518">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5519">
            Anti Mullerian Hormone (AMH) Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5520">
            <thead className="bg-gray-100" data-id="element-5521">
              <tr data-id="element-5522">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5523">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5524">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5525">
                  Reference (by age)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5526">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5527">
              <tr className="align-top" data-id="element-5528">
                <td className="border border-gray-300 p-1.5 font-medium" data-id="element-5529">
                  {amhStructure.name}
                </td>
                <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5530">
                  {lab.results![amhStructure.storageKey] || focusedTest && lab.results![focusedTest] || lab.results!['AMH'] || lab.results!['Anti-Müllerian Hormone'] || lab.results!['ANTI-MÜLLERIAN HORMONE'] || lab.results!['Anti Mullerian Hormone (AMH)'] || '—'}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5531">
                  {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-5532">
                      <span className="font-medium" data-id="element-5533">{a.ageBand}:</span>{' '}
                      {a.range}
                    </div>)}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5534">
                  {amhStructure.unit}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[10px] italic text-gray-600" data-id="element-5535">
            Method: {amhStructure.method}
          </p>
        </div>;
    }
    if (isHormonalProfileTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5536">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5537">
            Hormonal Profile Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5538">
            <thead className="bg-gray-100" data-id="element-5539">
              <tr data-id="element-5540">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5541">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5542">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5543">
                  Reference Range
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5544">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5545">
              {hormonalProfileTests.map(h => <tr key={h.key} data-id="element-5546">
                  <td className="border border-gray-300 p-1.5 font-medium" data-id="element-5547">
                    {h.name}
                  </td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5548">
                    {lab.results![`HP_${h.key}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5549">
                    {h.ranges.map(r => <div key={r.label} data-id="element-5550">
                        <span className="font-medium" data-id="element-5551">{r.label}:</span>{' '}
                        {r.value}
                      </div>)}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5552">
                    {h.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    // Fallback: generic key/value table
    return <div className="text-sm" data-id="element-5553">
        <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5554">
          {activeTests.join(', ')} Report
        </h4>
        <table className="w-full text-xs border border-gray-300" data-id="element-5555">
          <thead className="bg-gray-100" data-id="element-5556">
            <tr data-id="element-5557">
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-5558">
                Parameter
              </th>
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-5559">Result</th>
            </tr>
          </thead>
          <tbody data-id="element-5560">
            {Object.entries(lab.results).map(([k, v]) => v ? <tr key={k} data-id="element-5561">
                  <td className="border border-gray-300 p-1.5" data-id="element-5562">{k}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5563">
                    {v}
                  </td>
                </tr> : null)}
          </tbody>
        </table>
      </div>;
  };
  const renderRadReport = (r: RadiologyRequest) => <div className="text-sm" data-id="element-5564">
      <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5565">
        Radiology Report — {r.scanType}
      </h4>
      {r.completedAt && <p className="text-xs text-gray-500 mb-2" data-id="element-5566">
          Completed: {new Date(r.completedAt).toLocaleString()}
        </p>}
      <div className="bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap text-sm" data-id="element-5567">
        {r.report || 'No report available.'}
      </div>
      {r.images && r.images.length > 0 && <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-5568">
          {r.images.map((img, idx) => <img key={idx} src={img} alt={`${r.scanType} ${idx + 1}`} className="rounded border border-gray-200 object-cover w-full max-h-64" data-id="element-5569" />)}
        </div>}
    </div>;
  return <>
      <Card data-id="element-5570">
        <div className="flex justify-between items-center mb-4 print:hidden" data-id="element-5571">
          <div className="flex items-center gap-2 flex-1 max-w-md" data-id="element-5572">
            <div className="relative w-full" data-id="element-5573">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5574">
                <Search className="h-4 w-4 text-gray-400" data-id="element-5575" />
              </div>
              <input type="text" placeholder="Search by patient name or BMC..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-5576" />
            </div>
          </div>
          <Badge variant="info" className="text-sm" data-id="element-5577">
            {filtered.length} report{filtered.length === 1 ? '' : 's'}
          </Badge>
        </div>

        {filtered.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-5578">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-3" data-id="element-5579" />
            <p className="text-lg font-medium text-gray-900" data-id="element-5580">No reports yet</p>
            <p className="text-sm" data-id="element-5581">
              Completed lab and radiology reports will appear here.
            </p>
          </div> : <ul className="divide-y divide-gray-200" data-id="element-5582">
            {filtered.map(enc => {
          const patient = getPatient(enc.patientId);
          if (!patient) return null;
          const completedLabs = enc.labRequests.filter(l => l.status === 'Completed');
          const completedRads = enc.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0);
          return <li key={enc.id} className="py-4 flex justify-between items-start gap-4" data-id="element-5583">
                  <div className="flex-1" data-id="element-5584">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-5585">
                      <p className="text-base font-bold text-gray-900" data-id="element-5586">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <Badge variant="info" data-id="element-5587">BMC: {patient.bmcFileNumber}</Badge>
                      <span className="text-xs text-gray-500" data-id="element-5588">
                        {patient.age} yrs · {patient.sex}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-5589">
                      Visit: {new Date(enc.startedAt).toLocaleString()}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-5590">
                      {completedLabs.length > 0 && <span className="inline-flex items-center text-xs bg-yellow-50 text-yellow-800 px-2 py-1 rounded border border-yellow-200" data-id="element-5591">
                          <FlaskConical className="w-3 h-3 mr-1" data-id="element-5592" />
                          {completedLabs.length} Lab Report
                          {completedLabs.length > 1 ? 's' : ''}
                        </span>}
                      {completedRads.length > 0 && <span className="inline-flex items-center text-xs bg-purple-50 text-purple-800 px-2 py-1 rounded border border-purple-200" data-id="element-5593">
                          <Scan className="w-3 h-3 mr-1" data-id="element-5594" />
                          {completedRads.length} Radiology Report
                          {completedRads.length > 1 ? 's' : ''}
                        </span>}
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => setViewing({
              encounter: enc
            })} data-id="element-5595">
                    <FileText className="w-4 h-4 mr-2" data-id="element-5596" /> View Report
                  </Button>
                </li>;
        })}
          </ul>}
      </Card>

      {viewing && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-5597">
          <div className="min-h-full flex items-start justify-center p-4" data-id="element-5598">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-5599">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white z-10 print:hidden" data-id="element-5600">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5601">
                  Patient Report
                </h3>
                <div className="flex items-center gap-2" data-id="element-5602">
                  <Button variant="outline" onClick={handlePrint} data-id="element-5603">
                    <Printer className="w-4 h-4 mr-2" data-id="element-5604" /> Print
                  </Button>
                  <Button variant="outline" onClick={handleDownloadPDF} data-id="element-5605">
                    <Download className="w-4 h-4 mr-2" data-id="element-5606" /> Download PDF
                  </Button>
                  <button onClick={() => setViewing(null)} data-id="element-5607">
                    <X className="w-5 h-5 text-gray-400" data-id="element-5608" />
                  </button>
                </div>
              </div>
              <div id="report-printable" className="p-6 overflow-y-auto flex-1" data-id="element-5609">
                {(() => {
              const patient = getPatient(viewing.encounter.patientId);
              if (!patient) return null;
              return <>
                      <div className="border-b-2 border-black pb-3 mb-4" data-id="element-5610">
                        <h2 className="text-xl font-bold uppercase" data-id="element-5611">
                          Patient Report
                        </h2>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm" data-id="element-5612">
                          <p data-id="element-5613">
                            <span className="font-semibold" data-id="element-5614">Patient:</span>{' '}
                            {patient.firstName} {patient.lastName}
                          </p>
                          <p data-id="element-5615">
                            <span className="font-semibold" data-id="element-5616">BMC #:</span>{' '}
                            {patient.bmcFileNumber}
                          </p>
                          <p data-id="element-5617">
                            <span className="font-semibold" data-id="element-5618">Age:</span>{' '}
                            {patient.age} yrs
                          </p>
                          <p data-id="element-5619">
                            <span className="font-semibold" data-id="element-5620">Sex:</span>{' '}
                            {patient.sex}
                          </p>
                          <p className="col-span-2" data-id="element-5621">
                            <span className="font-semibold" data-id="element-5622">Visit date:</span>{' '}
                            {new Date(viewing.encounter.startedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').map(lab => <div key={lab.id} className="mb-6" data-id="element-5623">
                            {renderLabResults(lab)}
                            {lab.labComments && <div className="mt-4 border border-gray-300 bg-gray-50 rounded p-3" data-id="element-5624">
                                <h5 className="font-bold uppercase mb-1 text-xs" data-id="element-5625">
                                  Comments / Observations
                                </h5>
                                <pre className="whitespace-pre-wrap font-sans text-xs text-gray-800" data-id="element-5626">
                                  {lab.labComments}
                                </pre>
                              </div>}
                          </div>)}

                      {viewing.encounter.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0).map(r => <div key={r.id} className="mb-6" data-id="element-5627">
                            {renderRadReport(r)}
                          </div>)}

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').length === 0 && viewing.encounter.radiologyRequests.filter(r => r.report).length === 0 && <p className="text-gray-500 text-center py-8" data-id="element-5628">
                            No reports available for this encounter.
                          </p>}
                    </>;
            })()}
              </div>
            </div>
          </div>
        </div>}

      {/* Print-specific styling */}
      <style data-id="element-5629">{`
        @media print {
          body * { visibility: hidden; }
          #report-printable, #report-printable * { visibility: visible; }
          #report-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
        }
      `}</style>
    </>;
};